import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Plus, Lock, SlidersHorizontal, UsersRound } from 'lucide-react';
import { RoleBuilderDialog, RolesPanel } from '@/components/team/RoleManagement';
import { TeamAccountDialogs } from '@/components/team/TeamAccountDialogs';
import { TeamMembersList } from '@/components/team/TeamMembersList';
import { HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionResources, setPermissionResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showRoleBuilder, setShowRoleBuilder] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: {}, dba_ids: [] });
  const [showResetPw, setShowResetPw] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showEditMember, setShowEditMember] = useState(null);
  const [error, setError] = useState('');

  const fetchTeam = useCallback(async () => {
    try {
      const [teamResponse, rolesResponse] = await Promise.all([api.get('/team'), api.get('/team/roles')]);
      setMembers(teamResponse.data);
      setRoles(rolesResponse.data);
    } catch { return; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    api.get('/team/permission-resources')
      .then(({ data }) => setPermissionResources(data.resources || []))
      .catch(() => setPermissionResources([]));
  }, [user]);

  const handleInvite = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    const f = new FormData(e.target);
    try {
      await api.post('/team/invite', {
        name: f.get('name'),
        email: f.get('email'),
        username: f.get('username'),
        phone: f.get('phone'),
        role: f.get('role'),
        password: f.get('password'),
      });
      setShowInvite(false);
      fetchTeam();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  }, [fetchTeam]);

  const updateRole = useCallback(async (userId, role) => {
    try {
      await api.put(`/team/${userId}/role`, { role });
      fetchTeam();
    } catch { return; }
  }, [fetchTeam]);

  const removeMember = useCallback(async (userId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await api.delete(`/team/${userId}`);
      fetchTeam();
    } catch { return; }
  }, [fetchTeam]);

  const resetPassword = useCallback(async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post(`/team/${showResetPw.id}/reset-password`, { password: f.get('password') });
      setShowResetPw(null);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  }, [showResetPw]);

  const changeOwnPassword = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    const f = new FormData(e.target);
    if (f.get('new_password') !== f.get('confirm_password')) {
      setError('Passwords do not match');
      return;
    }
    try {
      await api.post('/auth/change-password', { current_password: f.get('current_password'), new_password: f.get('new_password') });
      setShowChangePw(false);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  }, []);

  const toggleActive = useCallback(async (userId, currentlyActive) => {
    await api.put(`/team/${userId}/status`, { is_active: !currentlyActive });
    fetchTeam();
  }, [fetchTeam]);

  const saveMemberProfile = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    const f = new FormData(e.target);
    try {
      await api.put(`/team/${showEditMember.id}/profile`, {
        name: f.get('name'),
        email: f.get('email'),
        username: f.get('username'),
        phone: f.get('phone'),
        notification_preferences: {
          new_lead_sms: f.get('new_lead_sms') === 'on',
          quote_status_email: f.get('quote_status_email') === 'on',
          appointment_sms: f.get('appointment_sms') === 'on',
        },
      });
      setShowEditMember(null);
      fetchTeam();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  }, [fetchTeam, showEditMember]);

  const isAdmin = user?.role === 'admin';

  const openNewRole = useCallback(() => {
    setError('');
    setEditingRole(null);
    setRoleForm({ name: '', description: '', permissions: {}, dba_ids: [] });
    setShowRoleBuilder(true);
  }, []);

  const openEditRole = useCallback((role) => {
    setError('');
    setEditingRole(role);
    setRoleForm({ name: role.name || '', description: role.description || '', permissions: role.permissions || {}, dba_ids: role.dba_ids || [] });
    setShowRoleBuilder(true);
  }, []);

  const togglePermission = useCallback((resource, action) => {
    setRoleForm((current) => {
      const existing = current.permissions?.[resource] || [];
      const nextActions = existing.includes(action) ? existing.filter((item) => item !== action) : [...existing, action];
      const nextPermissions = { ...current.permissions, [resource]: nextActions };
      if (nextActions.length === 0) delete nextPermissions[resource];
      return { ...current, permissions: nextPermissions };
    });
  }, []);

  const saveRole = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingRole?.slug) {
        await api.put(`/team/roles/${editingRole.slug}`, roleForm);
      } else {
        await api.post('/team/roles', roleForm);
      }
      setShowRoleBuilder(false);
      fetchTeam();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  }, [editingRole, fetchTeam, roleForm]);

  const deleteRole = useCallback(async (roleSlug) => {
    if (!window.confirm('Archive this custom role?')) return;
    try {
      await api.delete(`/team/roles/${roleSlug}`);
      fetchTeam();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  }, [fetchTeam]);

  const teamActions = useMemo(() => ({
    updateRole,
    removeMember,
    toggleActive,
    editMember: (member) => { setError(''); setShowEditMember(member); },
    resetPassword: (member) => { setError(''); setShowResetPw(member); },
  }), [removeMember, toggleActive, updateRole]);

  const dialogState = useMemo(() => ({ showInvite, showEditMember, showResetPw, showChangePw }), [showChangePw, showEditMember, showInvite, showResetPw]);
  const dialogSetters = useMemo(() => ({ setShowInvite, setShowEditMember, setShowResetPw, setShowChangePw }), []);
  const dialogHandlers = useMemo(() => ({ handleInvite, saveMemberProfile, resetPassword, changeOwnPassword }), [changeOwnPassword, handleInvite, resetPassword, saveMemberProfile]);

  return (
    <div className="-mx-3 space-y-6 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0">
      <HighVisibilityPageHeader
        eyebrow="Access control"
        title="Team"
        subtitle="Manage users, passwords, roles, and who can access each workflow."
        countLabel={`${members.length} members · ${roles.length} roles`}
        icon={UsersRound}
        tone="blue"
        testId="team-high-visibility-header"
        titleTestId="team-title"
        actions={isAdmin ? (
          <>
            <Button variant="outline" className="h-14 rounded-2xl border-white/30 bg-white/20 px-4 font-black text-white hover:bg-white/30" onClick={() => { setError(''); setShowChangePw(true); }} data-testid="change-password-button"><Lock className="w-5 h-5 mr-2" /> My Password</Button>
            <Button variant="outline" className="h-14 rounded-2xl border-white/30 bg-white/20 px-4 font-black text-white hover:bg-white/30" onClick={openNewRole} data-testid="create-role-button"><SlidersHorizontal className="w-5 h-5 mr-2" /> New Role</Button>
            <Button className="h-14 rounded-2xl bg-white px-4 font-black text-blue-700 hover:bg-blue-50" onClick={() => setShowInvite(true)} data-testid="invite-member-button"><Plus className="w-5 h-5 mr-2" /> Add Member</Button>
          </>
        ) : (
          <Button variant="outline" className="h-14 rounded-2xl border-white/30 bg-white/20 px-4 font-black text-white hover:bg-white/30" onClick={() => { setError(''); setShowChangePw(true); }} data-testid="change-password-button"><Lock className="w-5 h-5 mr-2" /> Change Password</Button>
        )}
      />

      <div className="space-y-6 px-3 sm:px-0">

      <TeamMembersList loading={loading} members={members} roles={roles} isAdmin={isAdmin} currentUserId={user?.id} actions={teamActions} />

      {isAdmin && (
        <RolesPanel roles={roles} openNewRole={openNewRole} openEditRole={openEditRole} deleteRole={deleteRole} />
      )}

      <TeamAccountDialogs state={dialogState} setters={dialogSetters} handlers={dialogHandlers} roles={roles} error={error} />

      <RoleBuilderDialog open={showRoleBuilder} setOpen={setShowRoleBuilder} editingRole={editingRole} error={error} roleForm={roleForm} setRoleForm={setRoleForm} permissionResources={permissionResources} togglePermission={togglePermission} saveRole={saveRole} />

      </div>
    </div>
  );
}
